import React, {Component, ReactNode} from 'react'
import {AdminPagePropsSet, createAdminPage} from '../shared/hoc/createAdminPage'
import {Nullable} from '../../app/TypeHelpers'
import {NoSelectedValue} from '../../componentHelper/selectHelper'
import {MeetingRepositoryForAdmin} from './MeetingRepositoryForAdmin'
import {
    PageFooterArea,
    PageFotterAreaLeft,
    PageFotterAreaRight,
    PageMainArea,
} from '../shared/hoc/withAdminPageTemplate'
import NewMeeting from './model/NewMeeting'
import Time from '../../dateHelpers/Time'
import {Messages} from '../../Messages'
import {formatForDateInputFromDate} from '../../formatters/formatDate'
import {BackLink} from '../../styledComponents/adminPage/Links'
import {MeetingFile} from './model/MeetingFile'
import {AdminMeetingForm, AdminMeetingFormProps} from './AdminMeetingForm'
import {AdminPrimaryButton} from '../../styledComponents/adminPage/Buttons'
import {MESSAGE_TYPE, VALID_OR_NOT} from '../../CodeMaster'
import {RequestItemError} from '../../network/response/RequestItemError'

interface Props {
    condominiumCd: string
    meetingRepoForAdmin: MeetingRepositoryForAdmin
}

interface State {
    meetingDataSet: AdminMeetingFormProps
}

class AdminRegisterMeetingPage extends Component<Props & AdminPagePropsSet, State> {
    constructor(props: Readonly<Props & AdminPagePropsSet>) {
        super(props)

        this.state = {
            meetingDataSet: {
                name: '',
                date: null,
                startHour: NoSelectedValue,
                startMinute: NoSelectedValue,
                endHour: NoSelectedValue,
                endMinute: NoSelectedValue,
                isErrorOfPeriod: false,
                url: '',
                place: '',
                hasWebMeeting: false,
                memo: '',
                contents: '',
                attendanceReplyDeadline: null,
                manageAttendance: false,
                proxyAttendanceAvailability: null,
                registeredMeetingFileList: [],
                registeredMeetingLinkList: [],
                registeredMeetingReportList: [],
                deleteMeetingFileNumberList: [],
                deleteReportNumberList: [],
                version: 1,
            },
        }

        this.handleClickRegisterButton = this.handleClickRegisterButton.bind(this)
        this.handleRegisterOkClick = this.handleRegisterOkClick.bind(this)
        this.handleRegisterCancelClick = this.handleRegisterCancelClick.bind(this)
        this.handleChangeMeetingDataSet = this.handleChangeMeetingDataSet.bind(this)
    }

    handleClickRegisterButton(): void {
        this.props.eventEmitter.showDialog({
                title: '確認',
                errorLevel: Messages.IOPCC0012.errorLevel,
                message: Messages.IOPCC0012.toString('会議'),
                buttonProps: [
                    {
                        isDefault: false,
                        buttonLabel: 'キャンセル',
                        buttonHandler: this.handleRegisterCancelClick,
                    },
                    {
                        isDefault: false,
                        buttonLabel: 'OK',
                        buttonHandler: this.handleRegisterOkClick,
                    },
                ],
            },
        )
    }

    handleRegisterCancelClick(): void {
        this.props.eventEmitter.closeDialog()
    }

    async handleRegisterOkClick(): Promise<void> {
        const {condominiumCd} = this.props
        const {
            name,
            date,
            endHour,
            endMinute,
            startHour,
            startMinute,
            url,
            place,
            hasWebMeeting,
            memo,
            contents,
            attendanceReplyDeadline,
            manageAttendance,
            proxyAttendanceAvailability,
            registeredMeetingFileList,
            registeredMeetingLinkList,
            registeredMeetingReportList,
        } = this.state.meetingDataSet

        this.props.eventEmitter.closeDialog()

        if (name === null) {
            return
        }

        const newMemo = memo || null
        const newContents = contents || null

        let newUrl: Nullable<string> = null
        if (hasWebMeeting) {
            newUrl = url || null
        }
        const newPlace = place || null

        const meetingAttachmentFileList: MeetingFile[] = []
        if (registeredMeetingFileList) {
            registeredMeetingFileList.map((meetingFile) => {
                const fileData: string[] = String(meetingFile.fileData).split(',')
                if (fileData[1]) {
                    meetingAttachmentFileList.push({
                        fileNumber: meetingFile.fileNumber,
                        fileName: meetingFile.fileName,
                        fileData: fileData[1],
                        fileSize: meetingFile.fileSize,
                    })
                }
            })
        }

        const meetingReportList: MeetingFile[] = []
        if (registeredMeetingReportList) {
            registeredMeetingReportList.map((meetingReport) => {
                const fileData: string[] = String(meetingReport.fileData).split(',')
                if (fileData[1]) {
                    meetingReportList.push({
                        fileNumber: meetingReport.fileNumber,
                        fileName: meetingReport.fileName,
                        fileData: fileData[1],
                        fileSize: meetingReport.fileSize,
                    })
                }
            })
        }

        let startTime: Nullable<string> = null
        if (date !== null && startHour !== NoSelectedValue && startMinute !== NoSelectedValue) {
            startTime = new Time(Number(startHour), Number(startMinute)).toString()
        }

        let endTime: Nullable<string> = null
        if (date !== null && endHour !== NoSelectedValue && endMinute !== NoSelectedValue) {
            endTime = new Time(Number(endHour), Number(endMinute)).toString()
        }

        let manageAttendanceString: string = VALID_OR_NOT.NOT.value
        let attendanceReplyDeadlineString: Nullable<string> = null
        let proxyAttendanceAvailabilityString: Nullable<string> = null
        if (date !== null && manageAttendance) {
            if (attendanceReplyDeadline === null) {
                return
            }
            manageAttendanceString = VALID_OR_NOT.VALID.value
            attendanceReplyDeadlineString = formatForDateInputFromDate(attendanceReplyDeadline)
            proxyAttendanceAvailabilityString = proxyAttendanceAvailability
        }

        const newMeeting = new NewMeeting(
            name,
            !date ? null : formatForDateInputFromDate(date),
            startTime,
            endTime,
            newUrl,
            newPlace,
            newMemo,
            newContents,
            attendanceReplyDeadlineString,
            manageAttendanceString,
            proxyAttendanceAvailabilityString,
            registeredMeetingLinkList,
        )

        const nextParam =
            await this.registerMeetingAndRelated(newMeeting, meetingAttachmentFileList, meetingReportList)
        if (nextParam) {
            this.props.navigate(
                `/admin/condominiums/${condominiumCd}/meetings`,
                {state: nextParam},
            )
        }
    }

    async registerMeetingAndRelated(
        newMeeting: NewMeeting,
        meetingAttachmentFileList: MeetingFile[],
        meetingReportList: MeetingFile[],
    ): Promise<any> {       // TODO ReactVUP:型をAdminMeetingListPageからimportする
        const {condominiumCd, meetingRepoForAdmin, asyncCallFunction, eventEmitter} = this.props

        await eventEmitter.showLoading()
        try {
            const fileCount = meetingAttachmentFileList.length + meetingReportList.length
            if (fileCount !== 0) {
                const fileNameList: string[] = []
                meetingAttachmentFileList.forEach(file => fileNameList.push(file.fileName))
                meetingReportList.forEach(file => fileNameList.push(file.fileName))
                const fileDataForValidate = {
                    fileNameList: fileNameList,
                }
                const fileValidateResult = await asyncCallFunction(
                    meetingRepoForAdmin.validateParallelFileUpload.bind(
                        meetingRepoForAdmin,
                        fileDataForValidate,
                    ),
                    async (httpErrorOrNetworkError) => {
                        await httpErrorOrNetworkError.broadcast(eventEmitter)
                        if (httpErrorOrNetworkError.responseBody) {
                            httpErrorOrNetworkError.responseBody.requestItemErrors.forEach(item => {
                                if (item.fields === undefined) {
                                    return
                                }
                                const workIndex = item.fields[0].split('[')[1]
                                const fileIndex = Number(workIndex.split(']')[0])

                                let resultCode: string
                                if (fileIndex >= meetingAttachmentFileList.length) {
                                    resultCode = 'reports[' + (fileIndex - meetingAttachmentFileList.length) + ']'
                                } else {
                                    resultCode = 'attachmentFiles[' + fileIndex + ']'
                                }

                                const requestItemError = new RequestItemError(
                                    resultCode,
                                    'ファイル名に機種依存文字が使用されています。ファイル名を変更し、資料の追加をやり直してください。',
                                    '',
                                )

                                eventEmitter.emit(requestItemError.resultCode, requestItemError)
                            })
                        }
                    },
                    {defaultErrorTitle: Messages.EOPCA0001.toString('会議')},
                )
                if (fileValidateResult === null) {
                    return
                }
            }

            const meetingNumberAndInformation = await asyncCallFunction(
                meetingRepoForAdmin.registerMeeting.bind(meetingRepoForAdmin, condominiumCd, newMeeting),
                async (httpErrorOrNetworkError) => {
                    await httpErrorOrNetworkError.broadcast(eventEmitter)
                },
                {defaultErrorTitle: Messages.EOPCA0001.toString('会議')},
            )
            if (!meetingNumberAndInformation) {
                return
            }

            const meetingId = meetingNumberAndInformation.details
            const information = meetingNumberAndInformation.information

            for (let fileIndex = 0; fileIndex < meetingAttachmentFileList.length; fileIndex++) {
                eventEmitter.changeLoadingMessage(`ファイルアップロード中... ${fileIndex + 1}/${fileCount}`)

                const meetingFile = meetingAttachmentFileList[fileIndex]
                try {
                    // AsyncCallFunctionによる標準の通信エラー処理を行わない
                    // 原因はなんであれファイルを再登録する必要があることをユーザーに伝える必要があるため、全ての例外を捕捉する
                    await meetingRepoForAdmin.registerMeetingFile(
                        condominiumCd,
                        meetingId,
                        meetingFile,
                    )
                } catch (e) {
                    const errorFiles: string[] = []
                    meetingAttachmentFileList.forEach((file, index) => {
                        if (index >= fileIndex) {
                            errorFiles.push(file.fileName)
                        }
                    })
                    meetingReportList.forEach(report => errorFiles.push(report.fileName))
                    const detailMessage = '以下のファイル登録に失敗しています。編集画面より再度登録してください。'

                    return {
                        initialMessage: {
                            title: information.message,
                            messageLevel: MESSAGE_TYPE.ERROR,
                            detail: detailMessage,
                            itemErrors: errorFiles,
                        },
                        meetingId: meetingId,
                    }
                }
            }

            for (let reportIndex = 0; reportIndex < meetingReportList.length; reportIndex++) {
                const currentFileCount = reportIndex + 1 + meetingAttachmentFileList.length
                eventEmitter.changeLoadingMessage(`ファイルアップロード中... ${currentFileCount}/${fileCount}`)

                const report = meetingReportList[reportIndex]
                try {
                    // AsyncCallFunctionによる標準の通信エラー処理を行わない
                    // 原因はなんであれファイルを再登録する必要があることをユーザーに伝える必要があるため、全ての例外を捕捉する
                    await meetingRepoForAdmin.registerMeetingReport(
                        condominiumCd,
                        meetingId,
                        report,
                    )
                } catch (e) {
                    const errorFiles: string[] = []
                    meetingReportList.forEach((file, index) => {
                        if (index >= reportIndex) {
                            errorFiles.push(file.fileName)
                        }
                    })
                    const detailMessage = '以下のファイル登録に失敗しています。編集画面より再度登録してください。'

                    return {
                        initialMessage: {
                            title: information.message,
                            messageLevel: MESSAGE_TYPE.ERROR,
                            detail: detailMessage,
                            itemErrors: errorFiles,
                        },
                        meetingId: meetingId,
                    }
                }
            }
            return {
                initialMessage: {
                    title: information.title,
                    messageLevel: information.type,
                    detail: information.message,
                },
                meetingId,
                needToReceiveNotification: true,
            }
        } finally {
            eventEmitter.closeLoading()
        }
    }

    handleChangeMeetingDataSet(meetingDataSet: AdminMeetingFormProps): void {
        this.setState({meetingDataSet})
    }

    render(): ReactNode {
        const {
            name,
            startHour,
            startMinute,
            endHour,
            endMinute,
            url,
            hasWebMeeting,
            isErrorOfPeriod,
            manageAttendance,
            attendanceReplyDeadline,
            proxyAttendanceAvailability,
        } = this.state.meetingDataSet
        const isEmptyOrCompletedTime = (
            startHour === NoSelectedValue &&
            startMinute === NoSelectedValue &&
            endHour === NoSelectedValue &&
            endMinute === NoSelectedValue
        ) || (
            startHour !== NoSelectedValue &&
            startMinute !== NoSelectedValue &&
            endHour !== NoSelectedValue &&
            endMinute !== NoSelectedValue
        )
        const isWebMeetingOk =
            !hasWebMeeting || url !== ''
        const isManageAttendanceParamsOk =
            !manageAttendance || (attendanceReplyDeadline !== null && proxyAttendanceAvailability !== null)
        const disabledRegisterButton: boolean =
            !name || !isEmptyOrCompletedTime || isErrorOfPeriod ||
            !isManageAttendanceParamsOk || !isWebMeetingOk

        return (<>
            <PageMainArea>
                <AdminMeetingForm
                    condominiumCd={this.props.condominiumCd}
                    meetingRepoForAdmin={this.props.meetingRepoForAdmin}
                    meetingDataSet={this.state.meetingDataSet}
                    eventEmitter={this.props.eventEmitter}
                    asyncCallFunction={this.props.asyncCallFunction}
                    onChange={this.handleChangeMeetingDataSet}
                />
            </PageMainArea>

            <PageFooterArea>
                <PageFotterAreaRight>
                    <AdminPrimaryButton
                        onClick={this.handleClickRegisterButton}
                        disabled={disabledRegisterButton}
                    >
                        登録する
                    </AdminPrimaryButton>
                </PageFotterAreaRight>

                <PageFotterAreaLeft>
                    <BackLink
                        to={`/admin/condominiums/${this.props.condominiumCd}/meetings`}
                    >
                        会議詳細へ戻る
                    </BackLink>
                </PageFotterAreaLeft>
            </PageFooterArea>
        </>)
    }
}

export default createAdminPage(AdminRegisterMeetingPage)
